import { Users } from '@services/user';
import { config } from '@services/config';
import { UserSearchResponse, UserSearchRequestQuery } from '@lio/api-types';
import { useOktaAuth } from '@okta/okta-react';
import { userQueryKey } from '@services/user/keys';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';

export const useSearchUser = ({
  name = '',
  email,
  userId,
  brokerageId,
  role,
  isCustomerUser,
  isActive,
  pageLimit,
  pageOffset,
}: UserSearchRequestQuery) => {
  const { authState } = useOktaAuth();
  const client = useClient();
  const params = new URLSearchParams();
  if (name) params.append('name', name);
  if (email) params.append('email', email);
  if (userId) params.append('userId', userId);
  if (brokerageId) params.append('brokerageId', brokerageId);
  if (role) params.append('role', role);
  if (isCustomerUser !== undefined) params.append('isCustomerUser', String(isCustomerUser));
  if (isActive !== undefined) params.append('isActive', String(isActive));
  if (pageLimit) params.append('pageLimit', String(pageLimit));
  if (pageOffset) params.append('pageOffset', String(pageOffset));

  const queryFn = async () => {
    const query = `user?${String(params)}`;
    return client.get(query).json<Users>();
  };

  const result = useQuery<UserSearchResponse, Error>({
    queryKey: userQueryKey.searchWithParams({
      name,
      email,
      userId,
      brokerageId,
      role,
      isCustomerUser,
      isActive,
      pageLimit,
      pageOffset,
    }),
    queryFn,
    placeholderData: keepPreviousData,
    refetchOnMount: (query) => query.state.status !== 'error',
    refetchOnWindowFocus: (query) => query.state.status !== 'error',
    retry: false,
    enabled: !!authState?.isAuthenticated,
    ...config,
  });

  return { ...result, user: result.data };
};
